import React, { useEffect, useMemo, useRef, useState } from 'react';
import BBALoader from '../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Search, Users, Mail, Link2, CheckCircle2, Minus, ToggleLeft, ToggleRight } from 'lucide-react';
import { isPortalAssignable } from '../utils/portalAssignable';
import { db, auth, storage } from '../firebase';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc, addDoc, query, where, orderBy } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { sendInviteEmail } from '../services/emailService';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import { useTheme } from '../contexts/ThemeContext';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import PortalBackground from '../components/PortalBackground';

const InternalUsers = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('users');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [avatarLoadErrors, setAvatarLoadErrors] = useState(() => new Set());
  const [isOwner, setIsOwner] = useState(false);
  const [togglingAssignableId, setTogglingAssignableId] = useState(null);
  const [avatarUploadUser, setAvatarUploadUser] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState(null);
  const avatarFileInputRef = useRef(null);
  
  const [inviteForm, setInviteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Member',
    team: 'General',
    message: ''
  });
  
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [teams] = useState([
    { name: 'General', description: 'Default team for all users' },
    { name: 'Sales', description: 'Handles all sales operations' },
    { name: 'Operations', description: 'Business operations team' },
    { name: 'Finance', description: 'Financial operations team' },
    { name: 'Technology', description: 'Technology and development team' },
    { name: 'Marketing', description: 'Manages marketing campaigns' },
    { name: 'HR', description: 'Human resources team' }
  ]);

  const roles = [
    { title: 'Owner', description: 'Full access to portal including settings' },
    { title: 'Executive', description: 'Can create, modify and delete any item' },
    { title: 'Member', description: 'Can view and modify assigned items' },
    { title: 'Contractor', description: 'Limited access to assigned projects' },
    { title: 'Accountant', description: 'Access to financial documents only' }
  ];

  // Per-user integrations stored on the internal_users doc as integrations.{key}.connected
  const integrationDefs = [
    { key: 'googleCalendar', label: 'Google Calendar', logo: '/gdata-icon.svg' },
    { key: 'gmail', label: 'Gmail', logo: '/gdata-icon.svg' },
    { key: 'calendly', label: 'Calendly', logo: null },
    { key: 'fathom', label: 'Fathom', logo: '/fathomlogo.png' },
    { key: 'jobtread', label: 'JobTread', logo: '/jtreadlogo.png' },
    { key: 'docusign', label: 'DocuSign', logo: '/docusignlogo.png' }
  ];

  const isIntegrationConnected = (user, key) => Boolean(user?.integrations?.[key]?.connected);

  const [inviteLink, setInviteLink] = useState('');
  const [inviteRecipient, setInviteRecipient] = useState('');
  const [showInviteDetailsModal, setShowInviteDetailsModal] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const loadCurrentInternalRole = async () => {
      try {
        const uid = auth.currentUser?.uid;
        if (!uid) {
          setIsOwner(false);
          return;
        }
        const snap = await getDoc(doc(db, 'internal_users', uid));
        const role = snap.exists() ? snap.data()?.role : null;
        setIsOwner(role === 'Owner');
      } catch (e) {
        console.error('Error loading current internal role:', e);
        setIsOwner(false);
      }
    };

    loadCurrentInternalRole();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch users
      const usersCollection = collection(db, 'internal_users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);

      // Fetch invites
      const invitesCollection = collection(db, 'userInvites');
      const invitesQuery = query(
        invitesCollection,
        where('status', '==', 'pending')
      );
      const invitesSnapshot = await getDocs(invitesQuery);
      const invitesList = invitesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      invitesList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setInvites(invitesList);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitInvite = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      const inviteData = {
        email: inviteForm.email,
        firstName: inviteForm.firstName,
        lastName: inviteForm.lastName,
        role: inviteForm.role,
        team: inviteForm.team,
        status: 'pending',
        invitedBy: auth.currentUser.uid,
        invitedByName: auth.currentUser.displayName || auth.currentUser.email,
        timestamp: new Date().toISOString(),
        message: inviteForm.message || ''
      };

      const inviteDoc = await addDoc(collection(db, 'userInvites'), inviteData);
      try {
        await sendInviteEmail({ inviteId: inviteDoc.id });
      } catch (emailError) {
        console.error('Error sending email:', emailError);
        
        // Create a shareable invite link as fallback
        const inviteLink = `${window.location.origin}/signup?invite=${inviteDoc.id}&inviteEmail=${encodeURIComponent(inviteForm.email)}`;
        
        // Show success message with the invite link
        setInviteLink(inviteLink);
        setInviteRecipient(inviteForm.firstName);
        setShowInviteLinkModal(true);
        
        setInvites(prevInvites => [{
          id: inviteDoc.id,
          ...inviteData
        }, ...prevInvites]);

        setInviteForm({
          firstName: '',
          lastName: '',
          email: '',
          role: 'Member',
          team: 'General',
          message: ''
        });
        setShowInviteModal(false);
        return;
      }

      setInvites(prevInvites => [{
        id: inviteDoc.id,
        ...inviteData
      }, ...prevInvites]);

      setInviteForm({
        firstName: '',
        lastName: '',
        email: '',
        role: 'Member',
        team: 'General',
        message: ''
      });
      setShowInviteModal(false);
    } catch (error) {
      console.error('Error creating invite:', error);
      setError('Failed to create invite. Please try again.');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await deleteDoc(doc(db, 'userInvites', inviteId));
      setInvites(prevInvites => prevInvites.filter(invite => invite.id !== inviteId));
    } catch (error) {
      console.error('Error canceling invite:', error);
      setError('Failed to cancel invite. Please try again.');
    }
  };

  const handleInviteRowClick = (invite) => {
    const inviteLink = `${window.location.origin}/signup?invite=${invite.id}&inviteEmail=${encodeURIComponent(invite.email)}`;
    setSelectedInvite(invite);
    setInviteLink(inviteLink);
    setInviteRecipient(invite.firstName);
    setShowInviteDetailsModal(true);
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await updateDoc(doc(db, 'internal_users', userId), updates);
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId ? { ...user, ...updates } : user
      ));
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user. Please try again.');
    }
  };

  // Show / hide this user in client-facing assignee pickers (questions, fires, roadmap tasks)
  const handleTogglePortalAssignable = async (user) => {
    if (!user?.id || togglingAssignableId) return;
    const next = !isPortalAssignable(user);
    try {
      setTogglingAssignableId(user.id);
      setError(null);
      await updateDoc(doc(db, 'internal_users', user.id), { portalAssignable: next });
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, portalAssignable: next } : u)));
    } catch (err) {
      console.error('Error updating portal assignee visibility:', err);
      setError('Failed to update portal assignee visibility. Please try again.');
    } finally {
      setTogglingAssignableId(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Delete directly from Firestore without calling Cloud Function
      await deleteDoc(doc(db, 'internal_users', userId));
      
      // Update UI immediately
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      
      // Show success message
      setSuccessMessage('User deleted successfully from the system.');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
      
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    const matchesTeam = selectedTeam === 'all' || user.team === selectedTeam;
    const matchesSearch = searchQuery === '' || 
      user.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesRole && matchesTeam && matchesSearch;
  });

  const getRoleColor = (role) => {
    const colors = {
      'Owner': 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200',
      'Executive': 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
      'Member': 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
      'Contractor': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
      'Accountant': 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
    };
    return colors[role] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const hasUserAvatar = (user) => Boolean(getUserAvatarUrl(user));

  const getUserAvatarUrl = (user) => {
    // Support a few common field names across auth/profile systems
    return (
      user?.profileImage ||
      user?.photoURL ||
      user?.photoUrl ||
      user?.profilePictureUrl ||
      user?.profilePicture ||
      user?.profilePic ||
      user?.avatarUrl ||
      user?.avatarURL ||
      user?.imageUrl ||
      user?.imageURL ||
      user?.profile?.photoURL ||
      user?.profile?.profileImage ||
      ''
    );
  };

  const handleStartAvatarUpload = (user) => {
    setAvatarUploadError(null);
    setAvatarUploadUser(user);
    // Open file picker immediately
    setTimeout(() => avatarFileInputRef.current?.click(), 0);
  };

  const handleAvatarFileSelected = async (e) => {
    const file = e.target.files?.[0];
    // allow re-selecting same file later
    e.target.value = '';

    if (!file || !avatarUploadUser) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAvatarUploadError('Please select a valid image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAvatarUploadError('Image size must be less than 5MB.');
      return;
    }

    try {
      setAvatarUploading(true);
      setAvatarUploadError(null);

      const storageRef = ref(storage, `profilePictures/${avatarUploadUser.id}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Store in the same field used by MyProfile for internal users
      await updateDoc(doc(db, 'internal_users', avatarUploadUser.id), {
        profileImage: downloadURL,
        // also set these for compatibility with other pickers across the app
        photoURL: downloadURL,
        lastUpdated: new Date().toISOString()
      });

      // Update UI immediately
      setUsers(prev =>
        prev.map(u => (u.id === avatarUploadUser.id ? { ...u, profileImage: downloadURL, photoURL: downloadURL } : u))
      );

      // Clear any previous image load failure for this user
      setAvatarLoadErrors(prev => {
        const next = new Set(prev);
        next.delete(avatarUploadUser.id);
        return next;
      });

      setAvatarUploadUser(null);
    } catch (err) {
      console.error('Error uploading internal user avatar:', err);
      setAvatarUploadError('Failed to upload image. Please try again.');
    } finally {
      setAvatarUploading(false);
    }
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <BackButton onClick={() => navigate('/crm/templates')} />
          <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Internal Users</h1>
          <AddButton onClick={() => setShowInviteModal(true)}>
            <Plus className="h-5 w-5" />
          </AddButton>
        </div>

        {error && (
          <div className={`mb-4 p-3 ${darkMode ? 'bg-red-900 border-red-700 text-red-200' : 'bg-red-100 border-red-400 text-red-700'} border rounded-xl`}>
            {error}
          </div>
        )}

        {successMessage && (
          <div className={`mb-4 p-3 ${darkMode ? 'bg-green-900 border-green-700 text-green-200' : 'bg-green-100 border-green-400 text-green-700'} border rounded-xl`}>
            {successMessage}
          </div>
        )}

        {/* Tabs */}
        <div className={`mb-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`${
                activeTab === 'users'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : `${darkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <Users className="h-5 w-5 inline-block mr-2" />
              Users
            </button>
            <button
              onClick={() => setActiveTab('invites')}
              className={`${
                activeTab === 'invites'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : `${darkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <Mail className="h-5 w-5 inline-block mr-2" />
              Pending Invites
            </button>
            <button
              onClick={() => setActiveTab('roles')}
              className={`${
                activeTab === 'roles'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : `${darkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <Users className="h-5 w-5 inline-block mr-2" />
              Roles
            </button>
            <button
              onClick={() => setActiveTab('integrations')}
              className={`${
                activeTab === 'integrations'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : `${darkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <Link2 className="h-5 w-5 inline-block mr-2" />
              Integrations
            </button>
          </nav>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4 items-center">
          <div className="flex-1">
            <div className="relative">
              <Search className={`h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  darkMode 
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-red-500' 
                    : 'border-gray-300 focus:border-red-500'
                }`}
              />
            </div>
          </div>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className={`px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white focus:border-red-500' 
                : 'border-gray-300 focus:border-red-500'
            }`}
          >
            <option value="all">All Roles</option>
            {roles.map(role => (
              <option key={role.title} value={role.title}>{role.title}</option>
            ))}
          </select>
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            className={`px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white focus:border-red-500' 
                : 'border-gray-300 focus:border-red-500'
            }`}
          >
            <option value="all">All Teams</option>
            {teams.map(team => (
              <option key={team.name} value={team.name}>{team.name}</option>
            ))}
          </select>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <BBALoader size={48} className="mx-auto" />
            <p className={`mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading...</p>
          </div>
        ) : activeTab === 'users' ? (
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}>
            <input
              type="file"
              ref={avatarFileInputRef}
              onChange={handleAvatarFileSelected}
              accept="image/*"
              className="hidden"
              disabled={!isOwner || avatarUploading}
            />
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Name</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Email</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Role</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Team</th>
                  <th className={`px-6 py-3 text-center text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} title="When on, clients can pick this person as an assignee for questions, fires and roadmap tasks">Portal Assignee</th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y divide-gray-200 dark:divide-gray-700`}>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() => {
                            if (!isOwner) return;
                            if (hasUserAvatar(user)) return;
                            handleStartAvatarUpload(user);
                          }}
                          disabled={!isOwner || hasUserAvatar(user) || avatarUploading}
                          title={
                            !isOwner
                              ? 'Only Owners can upload profile pictures'
                              : hasUserAvatar(user)
                                ? 'User already has a profile picture'
                                : 'Upload profile picture'
                          }
                          className={`flex-shrink-0 h-10 w-10 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} rounded-full flex items-center justify-center overflow-hidden ${
                            isOwner && !hasUserAvatar(user) && !avatarUploading
                              ? 'cursor-pointer hover:ring-2 hover:ring-red-500'
                              : 'cursor-default'
                          }`}
                        >
                          {getUserAvatarUrl(user) && !avatarLoadErrors.has(user.id) ? (
                            <img
                              src={getUserAvatarUrl(user)}
                              alt={`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User'}
                              className="h-10 w-10 rounded-full object-cover"
                              onError={() => {
                                setAvatarLoadErrors(prev => {
                                  const next = new Set(prev);
                                  next.add(user.id);
                                  return next;
                                });
                              }}
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} font-medium`}>
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </span>
                          )}
                        </button>
                        <div className="ml-4">
                          <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {user.firstName} {user.lastName}
                          </div>
                          {isOwner && !hasUserAvatar(user) && (
                            <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Click avatar to upload
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {user.team}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {(() => {
                        const enabled = isPortalAssignable(user);
                        const busy = togglingAssignableId === user.id;
                        return (
                          <button
                            type="button"
                            onClick={() => handleTogglePortalAssignable(user)}
                            disabled={busy}
                            aria-pressed={enabled}
                            title={enabled ? 'Visible to clients as an assignee — click to hide' : 'Hidden from clients — click to allow as an assignee'}
                            className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${busy ? 'opacity-50 cursor-wait' : ''} ${enabled ? 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300' : (darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600')}`}
                          >
                            {enabled ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
                            <span>{enabled ? 'On' : 'Off'}</span>
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowEditModal(true);
                        }}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 mr-4"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'invites' ? (
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}>
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                💡 Click on any row to view invite details and copy the invite link
              </p>
            </div>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Name</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Email</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Role</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Team</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Invited By</th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y divide-gray-200 dark:divide-gray-700`}>
                {invites.map(invite => (
                  <tr 
                    key={invite.id}
                    onClick={() => handleInviteRowClick(invite)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {invite.firstName} {invite.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{invite.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColor(invite.role)}`}>
                        {invite.role}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {invite.team}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {invite.invitedByName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInviteRowClick(invite);
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          View Link
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelInvite(invite.id);
                          }}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'roles' ? (
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
            <div className="p-6">
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>User Roles</h2>
              <div className="space-y-4">
                {roles.map((role) => (
                  <div key={role.title} className={`border rounded-xl p-4 border-gray-200 dark:border-gray-700`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{role.title}</h3>
                        <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{role.description}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleColor(role.title)}`}>
                        {role.title}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-x-auto`}>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>User</th>
                  {integrationDefs.map(integration => (
                    <th
                      key={integration.key}
                      className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        {integration.logo && (
                          <img src={integration.logo} alt={integration.label} className="h-5 w-5 object-contain" />
                        )}
                        <span>{integration.label}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y divide-gray-200 dark:divide-gray-700`}>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-10 w-10 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} rounded-full flex items-center justify-center overflow-hidden`}>
                          {getUserAvatarUrl(user) && !avatarLoadErrors.has(user.id) ? (
                            <img
                              src={getUserAvatarUrl(user)}
                              alt={`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User'}
                              className="h-10 w-10 rounded-full object-cover"
                              onError={() => {
                                setAvatarLoadErrors(prev => {
                                  const next = new Set(prev);
                                  next.add(user.id);
                                  return next;
                                });
                              }}
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <span className={`${darkMode ? 'text-gray-300' : 'text-gray-500'} font-medium`}>
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </span>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {user.firstName} {user.lastName}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    {integrationDefs.map(integration => {
                      const connected = isIntegrationConnected(user, integration.key);
                      return (
                        <td key={integration.key} className="px-4 py-4 whitespace-nowrap text-center">
                          {connected ? (
                            <span className="inline-flex flex-col items-center gap-1" title={`${integration.label}: Connected`}>
                              <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto" />
                              <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Connected</span>
                            </span>
                          ) : (
                            <span className="inline-flex flex-col items-center gap-1" title={`${integration.label}: Not connected`}>
                              <Minus className={`h-5 w-5 mx-auto ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Not connected</span>
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowInviteModal(false)}></div>
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl max-w-lg w-full mx-auto z-50 p-6 relative`}>
              <div className="flex justify-between items-center mb-4">
                <div className="flex-1"></div>
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>Invite New User</h2>
                <div className="flex-1 flex justify-end">
                  <button onClick={() => setShowInviteModal(false)} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}>&times;</button>
                </div>
              </div>
              <form onSubmit={handleSubmitInvite}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        value={inviteForm.firstName}
                        onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                        className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'border-gray-300'
                        }`}
                        required
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={inviteForm.lastName}
                        onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                        className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'border-gray-300'
                        }`}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'border-gray-300'
                      }`}
                      required
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Role</label>
                    <select
                      name="role"
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                      className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'border-gray-300'
                      }`}
                    >
                      {roles.map(role => (
                        <option key={role.title} value={role.title}>{role.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Team</label>
                    <select
                      name="team"
                      value={inviteForm.team}
                      onChange={(e) => setInviteForm({ ...inviteForm, team: e.target.value })}
                      className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'border-gray-300'
                      }`}
                    >
                      {teams.map(team => (
                        <option key={team.name} value={team.name}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Message (Optional)</label>
                    <textarea
                      name="message"
                      value={inviteForm.message}
                      onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                      rows="3"
                      className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className={`px-4 py-2 border rounded-md text-sm font-medium ${
                      darkMode 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Send Invite
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEditModal(false)}></div>
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl max-w-lg w-full mx-auto z-50 p-6 relative`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Edit User</h2>
                <button onClick={() => setShowEditModal(false)} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}>&times;</button>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleUpdateUser(selectedUser.id, {
                  role: selectedUser.role,
                  team: selectedUser.team
                });
              }}>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name</label>
                    <div className={`mt-1 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedUser.firstName} {selectedUser.lastName}
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
                    <div className={`mt-1 text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedUser.email}
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Role</label>
                    <select
                      value={selectedUser.role}
                      onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                      className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'border-gray-300'
                      }`}
                    >
                      {roles.map(role => (
                        <option key={role.title} value={role.title}>{role.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Team</label>
                    <select
                      value={selectedUser.team}
                      onChange={(e) => setSelectedUser({ ...selectedUser, team: e.target.value })}
                      className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'border-gray-300'
                      }`}
                    >
                      {teams.map(team => (
                        <option key={team.name} value={team.name}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className={`px-4 py-2 border rounded-md text-sm font-medium ${
                      darkMode 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Invite Link Modal */}
        {showInviteLinkModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowInviteLinkModal(false)}></div>
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl max-w-lg w-full mx-auto z-50 p-6 relative`}>
              <div className="flex justify-between items-center mb-4">
                <div className="flex-1"></div>
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>Invite Link</h2>
                <div className="flex-1 flex justify-end">
                  <button onClick={() => setShowInviteLinkModal(false)} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}>&times;</button>
                </div>
              </div>
              <div className="text-center">
                <p>{inviteRecipient}, please use the following link to sign up:</p>
                <p>{inviteLink}</p>
              </div>
            </div>
          </div>
        )}

        {/* Invite Details Modal */}
        {showInviteDetailsModal && selectedInvite && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowInviteDetailsModal(false)}></div>
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl max-w-2xl w-full mx-auto z-50 p-6 relative`}>
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Invite Details</h2>
                <button 
                  onClick={() => setShowInviteDetailsModal(false)} 
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}
                >
                  &times;
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Invite Information */}
                <div className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4`}>
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Invite Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Name</label>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedInvite.firstName} {selectedInvite.lastName}
                      </p>
                    </div>
                    <div>
                      <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Email</label>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedInvite.email}</p>
                    </div>
                    <div>
                      <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Role</label>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColor(selectedInvite.role)}`}>
                        {selectedInvite.role}
                      </span>
                    </div>
                    <div>
                      <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Team</label>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedInvite.team}</p>
                    </div>
                    <div>
                      <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Invited By</label>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedInvite.invitedByName}</p>
                    </div>
                    <div>
                      <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Invited On</label>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {new Date(selectedInvite.timestamp).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {selectedInvite.message && (
                    <div className="mt-4">
                      <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Message</label>
                      <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'} mt-1`}>{selectedInvite.message}</p>
                    </div>
                  )}
                </div>

                {/* Invite Link */}
                <div className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4`}>
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Invite Link</h3>
                  <div className="space-y-3">
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Share this link with {selectedInvite.firstName} to complete their account setup:
                    </p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={inviteLink}
                        readOnly
                        className={`flex-1 px-3 py-2 text-sm border rounded-md ${
                          darkMode 
                            ? 'bg-gray-600 border-gray-500 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(inviteLink);
                          setSuccessMessage('Invite link copied to clipboard!');
                          setTimeout(() => setSuccessMessage(null), 3000);
                        }}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                          darkMode
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowInviteDetailsModal(false)}
                  className={`px-4 py-2 border rounded-md text-sm font-medium ${
                    darkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowInviteDetailsModal(false);
                    handleCancelInvite(selectedInvite.id);
                  }}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  Cancel Invite
                </button>
              </div>
            </div>
          </div>
        )}

        {avatarUploadError && (
          <div className={`mt-4 p-3 ${darkMode ? 'bg-red-900 border-red-700 text-red-200' : 'bg-red-100 border-red-400 text-red-700'} border rounded-xl`}>
            {avatarUploadError}
          </div>
        )}
      </div>
    </div>
  );
};

export default InternalUsers; 